import { DateText, DetailList } from './ContentBase';
import moment from 'moment';
import {
  capitalize,
  formatAndStringify,
  stringifyReasons,
} from '../../../../../helpers';
import purpleDosageIcon from '../../../../../../assets/img/purpleDosageIcon.png';
import purpleChatIcon from '../../../../../../assets/img/purpleChat.png';
import {
  extractFHIRDisplay,
  extractSurveyDisplay,
} from '../../../../../components/Timeline/utils/timeline';
import { SurveyAnswers } from '../../../../../components/Timeline/types/TimelineItemTypes';

interface ContentBaseDataProps {
  insightDetailsContent: any;
  showFullDay?: boolean;
}

const formatDate = (date: string | null | undefined, format = 'DD MMM YY') =>
  date ? moment(date).format(format) : 'Not available';

export const MedicationContent = ({
  insightDetailsContent,
}: ContentBaseDataProps) => {
  const {
    settings,
    pd_dispense_record_link,
    event_date,
    medicinal_product_pack,
  } = insightDetailsContent;

  return pd_dispense_record_link ? renderDispenseContent() : renderPBSContent();

  function renderDispenseContent() {
    return (
      <>
        {settings?.dosageQty && <DateText date={event_date} />}
        <div className="h6">{medicinal_product_pack}</div>
        {settings?.dosageQty && (
          <div className={'group'}>
            <div>
              <img
                src={purpleDosageIcon}
                alt="Dosage Icon"
                className="insightImage"
              />
              Dosage: {settings?.dosageQty} {settings?.dosageFrequency}
            </div>
            {settings?.changeReasons && (
              <div>
                <img
                  src={purpleChatIcon}
                  alt="Chat Icon"
                  className="insightImage"
                />
                <span>
                  Reasons: {stringifyReasons(settings?.changeReasons)}
                </span>
              </div>
            )}
          </div>
        )}
        <MedicationDispenseContent
          insightDetailsContent={insightDetailsContent}
        />
      </>
    );
  }

  function renderPBSContent() {
    return (
      <>
        <div className="title">
          {medicinal_product_pack ??
            insightDetailsContent.translation_display_name ??
            insightDetailsContent.original_name}
        </div>
        <DetailList
          details={{
            Dispensed: formatDate(insightDetailsContent.start_time),
            Prescribed: formatDate(insightDetailsContent.when_prescribed),
            'Dosage instructions':
              insightDetailsContent.label_instruction ??
              insightDetailsContent.prescription_comment ??
              'Not available',
            Quantity: insightDetailsContent.quantity ?? 'Not available',
          }}
        />
        {insightDetailsContent.count > 0 && (
          <DetailList
            details={{
              'First script': formatDate(
                insightDetailsContent.earliest_when_dispensed,
              ),
              'Last script': formatDate(
                insightDetailsContent.latest_when_dispensed,
              ),
              'Pick ups': insightDetailsContent.count,
            }}
          />
        )}
      </>
    );
  }
};

export const DosageChangeContent = ({
  insightDetailsContent,
}: ContentBaseDataProps) => {
  const settings = insightDetailsContent.settings;
  if (!settings) return null;
  return (
    <>
      {settings.dosageQty && (
        <DateText date={insightDetailsContent.event_date} />
      )}
      <div className="h6">{insightDetailsContent.medicinal_product_pack}</div>
      {settings.dosageQty && (
        <div className={'group'}>
          <div>
            <span>
              <img
                src={purpleDosageIcon}
                alt="Dosage Icon"
                className="insightImage"
              />
              Dosage: {settings.dosageQty} {settings.dosageFrequency}
            </span>
          </div>

          {settings.changeReasons && (
            <div>
              <img
                src={purpleChatIcon}
                alt="Chat Icon"
                className="insightImage"
              />
              <span>Reasons: </span>
              {stringifyReasons(settings.changeReasons)}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export const GenericDetailContent = ({
  insightDetailsContent,
}: ContentBaseDataProps) => {
  return (
    <>
      <DateText date={insightDetailsContent.event_date} />
      <div className="title">{insightDetailsContent.event_name}</div>
      <ul className="details-list">
        {Object.entries(insightDetailsContent.detail)
          .filter(
            ([key]) =>
              key !== 'value' &&
              key !== 'description' &&
              key !== 'mpp_id' &&
              key !== 'mp_id' &&
              key !== 'enterRole' &&
              key !== 'id' &&
              key !== 'icon' &&
              key !== 'isPrn' &&
              key !== 'episodeNo' &&
              key !== 'survey' &&
              key !== 'scriptWritten',
          )
          .map(([key, value]) => {
            if (key === 'fhir') {
              const fhirDisplays = extractFHIRDisplay(
                value as Record<string, any>,
              );
              return fhirDisplays.map((display, idx) => (
                <li
                  key={`${display.title}-${idx}`}
                >{`${display.title}: ${display.value}`}</li>
              ));
            } else if (key === 'answers') {
              const surveyDisplay = extractSurveyDisplay(
                value as SurveyAnswers[],
              );
              return surveyDisplay.map((display, idx) => (
                <li key={`${display.title}-${idx}`}>
                  <b>{display.title}</b>
                  <br />
                  {display.value}
                  <br />
                </li>
              ));
            } else if (key == 'initialSession') {
              return <li key={key}>{`Order type: ${value}`}</li>;
            } else if (key == 'alertValue') {
              return <li key={key}>{`Alert value: ${value}`}</li>;
            } else if (key == 'type') {
              return <li key={key}>{`Type: ${value}`}</li>;
            } else if (key == 'status') {
              return <li key={key}>{`Status: ${value}`}</li>;
            } else if (key == 'unit') {
              return <li key={key}>{`Unit: ${value}`}</li>;
            } else if (key == 'homeMedication' && value) {
              return <li key={key}>{`Home medication`}</li>;
            } else if (key == 'emrOrder' && value) {
              return <li key={key}>{`EMR order`}</li>;
            } else if (key == 'isStat' && value) {
              return <li key={key}>{`STAT`}</li>;
            } else if (value) {
              return <li key={key}>{`${key}: ${value}`}</li>;
            }
          })}
      </ul>
      {insightDetailsContent.detail.description ? (
        <div className="description">
          {insightDetailsContent.detail.description}
        </div>
      ) : (
        <>
          <div className="description">
            <p>{insightDetailsContent.description}</p>
          </div>
        </>
      )}
    </>
  );
};

export const PrescriptionContent = ({
  insightDetailsContent,
}: ContentBaseDataProps) => {
  return (
    <>
      <div className="title">{insightDetailsContent.mpp_pt}</div>
      <DetailList
        details={{
          Prescribed: formatDate(insightDetailsContent.prescription_written),
          'Dosage instructions':
            insightDetailsContent.label_instruction ??
            insightDetailsContent.prescription_comment ??
            'Not available',
          Quantity: insightDetailsContent.quantity ?? 'Not available',
        }}
      />
    </>
  );
};

const MedicationDispenseContent = ({
  insightDetailsContent,
}: ContentBaseDataProps) => {
  return (
    <>
      <div className="title">{insightDetailsContent.original_name}</div>
      <ul className="details-list">
        <li>
          Dispensed:{' '}
          {moment(insightDetailsContent.latest_dispense).format('DD MMM YY')}
        </li>
        <li>
          Prescribed:{' '}
          {insightDetailsContent.when_prescribed
            ? moment(insightDetailsContent.when_prescribed).format('DD MMM YY')
            : 'Not Available'}{' '}
        </li>
        <li>
          Dosage instructions:{' '}
          {insightDetailsContent.label_instruction ??
            insightDetailsContent.prescription_comment ??
            'Not available'}
        </li>
        <li>
          Quantity: {insightDetailsContent.dispatch_quantity ?? 'Not available'}
        </li>
      </ul>
      <ul className="details-list">
        <li>
          First script:{' '}
          {moment(insightDetailsContent.earliest_when_dispensed).format(
            'DD MMM YY',
          )}{' '}
        </li>
        <li>
          Last script:{' '}
          {moment(insightDetailsContent.latest_when_dispensed).format(
            'DD MMM YY',
          )}{' '}
        </li>
        <li>Pick ups: {insightDetailsContent.count}</li>
      </ul>
    </>
  );
};

export const PathologyDiagnosticContent = ({
  insightDetailsContent,
}: ContentBaseDataProps) => {
  return insightDetailsContent.document_id ? (
    <>
      <DateText date={insightDetailsContent.cda_effective_time} />
      <div className="title">{capitalize('Pathology Results')}</div>
      <div className="reportName">
        Report name:{' '}
        {insightDetailsContent.report_name
          ? ' ' + insightDetailsContent.report_name
          : ' Not available'}
      </div>
      <div className="testResultStatus">
        Test result status:{' '}
        {insightDetailsContent.test_result_status
          ? ' ' + insightDetailsContent.test_result_status
          : ' Not available'}
      </div>
      Test results:{' '}
      {insightDetailsContent.details ? (
        <ul className="details-list">
          {Object.entries(insightDetailsContent.details)
            .filter(
              ([key]) =>
                key !== 'value' && key !== 'description' && key !== 'raw',
            )
            .map(([key, value]) => (
              <li key={key}>{`${key} : ${value}`}</li>
            ))}
        </ul>
      ) : (
        'Not available'
      )}
      <div className="specimenCollectionDate">
        Specimen collection date:
        {insightDetailsContent.specimen_collection_date
          ? ' ' +
            moment(insightDetailsContent.specimen_collection_date).format(
              'DD MMM YY',
            )
          : ' Not available'}
      </div>
      <div className="testReqDate">
        Test requested date:{' '}
        {insightDetailsContent.test_requested_date
          ? ' ' +
            moment(insightDetailsContent.test_requested_date).format(
              'DD MMM YY',
            )
          : ' Not available'}
      </div>
      <div className="testReqName">
        Test requester name:
        {insightDetailsContent.test_requester_name
          ? ' ' + insightDetailsContent.test_requester_name
          : ' Not available'}
      </div>
      <div className="reportAuthDate">
        Report authored date:
        {insightDetailsContent.report_authored_date
          ? ' ' +
            moment(insightDetailsContent.report_authored_date).format(
              'DD MMM YY',
            )
          : ' Not available'}
      </div>
      <div className="reportStatus">
        Report status:
        {insightDetailsContent.report_status
          ? ' ' + insightDetailsContent.report_status
          : ' Not available'}
      </div>
      <div className="reportPathOrg">
        Reporting pathologist organisation:
        {insightDetailsContent.reporting_pathologist_organisation_name
          ? ' ' + insightDetailsContent.reporting_pathologist_organisation_name
          : ' Not available'}
      </div>
      <div className="reportPath">
        Reporting pathologist:
        {insightDetailsContent.reporting_pathologist_name
          ? ' ' + insightDetailsContent.reporting_pathologist_name
          : ' Not available'}
      </div>
      <div className="dateAvlToConsumer">
        Date available to consumer:
        {insightDetailsContent.date_available_to_consumer
          ? ' ' +
            moment(insightDetailsContent.date_available_to_consumer).format(
              'DD MMM YY',
            )
          : ' Not available'}
      </div>
    </>
  ) : (
    <>
      <DateText date={insightDetailsContent.event_date} />
      <div className="title">
        {capitalize(insightDetailsContent.mbs_taxonomy)}
      </div>
      <ul className="details-list">
        <li>Service provider: {insightDetailsContent.service_provider}</li>
        {insightDetailsContent.code && (
          <li>MBS code: {insightDetailsContent.code}</li>
        )}
      </ul>
      <div className="description">
        <p>{insightDetailsContent.description}</p>
      </div>
    </>
  );
};

export const CommonContent = ({
  insightDetailsContent,
}: ContentBaseDataProps) => {
  if (insightDetailsContent.detail === null)
    return (
      <>
        <DateText date={insightDetailsContent.event_date} />
        <div>
          {insightDetailsContent.event_category}{' '}
          {insightDetailsContent.group_type}
        </div>
      </>
    );
  return (
    <>
      <DateText date={insightDetailsContent.event_date} />
      <div className="description">
        <p>{insightDetailsContent.description}</p>
      </div>
    </>
  );
};

export const MBSContent = ({ insightDetailsContent }: ContentBaseDataProps) => {
  return (
    <>
      <DateText date={insightDetailsContent.event_date} />
      <div className="title">
        {capitalize(insightDetailsContent.mbs_taxonomy)}
      </div>
      <ul className="details-list">
        <li>Service provider: {insightDetailsContent.service_provider}</li>
        {insightDetailsContent.code && (
          <li>MBS code: {insightDetailsContent.code}</li>
        )}
      </ul>
      <div className="description">
        <p>{insightDetailsContent.description}</p>
      </div>
    </>
  );
};

export const ProfileCheckContent = ({
  insightDetailsContent,
  showFullDay = false,
}: ContentBaseDataProps) => {
  return insightDetailsContent.checks.map((idc: any, index: number) => (
    <div key={`profile-check-${index}`}>
      <DateText
        date={idc.interaction_date}
        format={showFullDay ? 'dddd, MMMM Do YYYY, hh:mm:ss a' : 'hh:mm a'}
      />
      <div className="title">Interaction: {idc.interaction_type}</div>
      <div className="title">
        Clinician: {idc.clinician_first_name} {idc.clinician_last_name}
      </div>
      <br />
    </div>
  ));
};

export const WellbeingContent = ({
  insightDetailsContent,
}: ContentBaseDataProps) => {
  return (
    <>
      <DateText date={insightDetailsContent.event_date} />
      {insightDetailsContent.medication_setting_snapshot && (
        <>
          <div className="title">
            {(() => {
              const snapshot = formatAndStringify(
                insightDetailsContent.medication_setting_snapshot,
              );
              return snapshot.medicinalProductPack;
            })()}
          </div>
          <ul className="details-list">
            <div className="description">
              {/* Parse and handle the medication_setting_snapshot */}
              {(() => {
                // Assuming medication_setting_snapshot is a JSON string
                const snapshot = formatAndStringify(
                  insightDetailsContent.medication_setting_snapshot,
                );
                const settings = snapshot.settings;

                return (
                  <>
                    {settings && (
                      <>
                        <li>Requested dosage: {settings.dosageQty} per week</li>
                        <li>
                          Previous dosage: {insightDetailsContent.dosage} per
                          week
                        </li>
                        <li>
                          Change reasons: {insightDetailsContent.change_reasons}
                        </li>
                      </>
                    )}
                  </>
                );
              })()}
            </div>
          </ul>
        </>
      )}
      <ul className="details-list">
        {insightDetailsContent.life_event && (
          <div className="description">
            Life Events
            {insightDetailsContent.life_event.map(
              (sign: any, index: number) => (
                <li key={`lift-sign-${index}`}>{sign}</li>
              ),
            )}
          </div>
        )}
      </ul>
      <ul className="details-list">
        {insightDetailsContent.good_signs && (
          <div className="description">
            Good Signs
            {insightDetailsContent.good_signs.map(
              (sign: any, index: number) => (
                <li key={`good-sign-${index}`}>{sign}</li>
              ),
            )}
          </div>
        )}
      </ul>
      <ul className="details-list">
        {insightDetailsContent.warning_signs && (
          <div className="description">
            Warning Signs
            {insightDetailsContent.warning_signs.map(
              (sign: any, index: number) => (
                <li key={`warning-sign-${index}`}>{sign}</li>
              ),
            )}
          </div>
        )}
      </ul>
    </>
  );
};

export const MedProfileContent = ({
  insightDetailsContent,
}: ContentBaseDataProps) => {
  return (
    <>
      <DateText date={insightDetailsContent.event_date} />
      <div className="title">{insightDetailsContent.event_name}</div>
      <ul className="details-list">
        {Object.entries(insightDetailsContent.detail)
          .filter(
            ([key]) =>
              key !== 'value' &&
              key !== 'description' &&
              key !== 'id' &&
              key !== 'icon',
          )
          .map(([key, value]) => {
            if (key == 'profileStatus' && value) {
              return <li key={key}>{`${value}`}</li>;
            } else if (key == 'finalisedBy' && value) {
              return <li key={key}>{`Finalised by: ${value}`}</li>;
            } else if (key == 'regimen' && value) {
              return <li key={key}>{`${value}`}</li>;
            } else if (key == 'brand' && value) {
              return <li key={key}>{`Brand: ${value}`}</li>;
            } else if (key == 'indication' && value) {
              return <li key={key}>{`Indication: ${value}`}</li>;
            } else if (key == 'indicationDescription' && value) {
              return <li key={key}>{`Indication Description: ${value}`}</li>;
            } else if (key == 'furtherInformation' && value) {
              return <li key={key}>{`Further Information: ${value}`}</li>;
            } else {
              return <li key={key}>{`${key}: ${value}`}</li>;
            }
          })}
      </ul>
      {insightDetailsContent.detail.description ? (
        <div className="description">
          {insightDetailsContent.detail.description}
        </div>
      ) : (
        <>
          <div className="description">
            <p>{insightDetailsContent.description}</p>
          </div>
        </>
      )}
    </>
  );
};
